import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, TrendingUp, ReceiptText, ShieldCheck, ArrowUpRight, AlertTriangle, Sparkles, Wallet, PiggyBank, Landmark, Home, Car, Coins, Gem, Building2, ShoppingBag, HeartHandshake, FileSignature, Dumbbell, BedDouble, Music } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import CashFlowChart from "@/components/CashFlowChart";
import BudgetAlerts from "@/components/BudgetAlerts";

// One-glance page bundling: net worth, upcoming bills, and 18+ shortcuts.
// Backed by a single GET /api/life/dashboard so it loads in one round-trip.

const CATEGORY_META = {
  cash:       { label: "Cash",        Icon: Wallet,     tone: "text-emerald-600" },
  bank:       { label: "Bank",        Icon: Landmark,   tone: "text-blue-600" },
  gold:       { label: "Gold",        Icon: Coins,      tone: "text-amber-600" },
  silver:     { label: "Silver",      Icon: Gem,        tone: "text-stone-500" },
  shares:     { label: "Shares",      Icon: TrendingUp, tone: "text-violet-600" },
  crypto:     { label: "Crypto",      Icon: Sparkles,   tone: "text-cyan-600" },
  mutual_fund:{ label: "Mutual Fund", Icon: PiggyBank,  tone: "text-teal-600" },
  fd:         { label: "Fixed Deposit", Icon: PiggyBank,tone: "text-teal-700" },
  property:   { label: "Property",    Icon: Home,       tone: "text-orange-600" },
  vehicle:    { label: "Vehicle",     Icon: Car,        tone: "text-rose-600" },
  business:   { label: "Business",    Icon: Building2,  tone: "text-indigo-600" },
  other:      { label: "Other",       Icon: ShoppingBag,tone: "text-stone-500" },
  liability:  { label: "Liability",   Icon: AlertTriangle, tone: "text-red-600" },
};

const ADULT_SHORTCUTS = [
  { label: "Dating",       Icon: HeartHandshake, to: "/dating" },
  { label: "Matrimonial",  Icon: FileSignature,  to: "/search?category=matrimonial" },
  { label: "Gyms",         Icon: Dumbbell,       to: "/search?category=gym" },
  { label: "Hotels",       Icon: BedDouble,      to: "/search?category=hotel" },
  { label: "Concerts",     Icon: Music,          to: "/search?category=music" },
  { label: "All 18+ tiles", Icon: ShieldCheck,   to: "/explore-18" },
];

const inr = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function LifeDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useSeo({ title: "Life Dashboard · Find2Near", description: "Your net worth, upcoming bills, and adult shortcuts at a glance." });

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get("/life/dashboard").then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (authLoading || loading) return <div className="p-12 text-center text-stone-500"><Loader2 className="inline animate-spin mr-2" /> Loading your life…</div>;
  if (!user) return (
    <div className="max-w-lg mx-auto p-12 text-center">
      <div className="text-lg font-bold mb-2">Sign in to view your Life Dashboard</div>
      <Link to="/"><Button className="rounded-full">Go home</Button></Link>
    </div>
  );
  if (!data) return <div className="p-12 text-center text-stone-500">Couldn't load your dashboard. Refresh to try again.</div>;

  const { assets, bills, adult } = data;
  const netPositive = assets.net_worth >= 0;

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-6 space-y-6" data-testid="life-dashboard">
      <header>
        <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900 mb-1">Hi {data.user_name}, here's your life at a glance</h1>
        <p className="text-stone-500 text-sm">Net worth, upcoming bills, and your adult-verified shortcuts — all in one place.</p>
      </header>

      {/* Net worth hero */}
      <section className={`rounded-3xl p-6 md:p-7 text-white ${netPositive ? "bg-gradient-to-br from-[#2D5A27] to-[#1a3e17]" : "bg-gradient-to-br from-red-700 to-red-950"}`} data-testid="life-networth">
        <div className="text-[10px] uppercase tracking-widest font-bold text-white/70 mb-1">Net worth</div>
        <div className="text-4xl md:text-5xl font-black tracking-tight" data-testid="life-networth-value">{inr(assets.net_worth)}</div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
          <Stat label="Total assets" value={inr(assets.assets_total)} />
          <Stat label="Total liabilities" value={inr(assets.liabilities)} />
          <Stat label="Items tracked" value={assets.item_count} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/assets" className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 text-xs font-bold" data-testid="life-cta-assets">
            Open Assets & Liabilities <ArrowUpRight size={12} />
          </Link>
        </div>
      </section>

      {/* Assets category breakdown */}
      {Object.keys(assets.categories).length > 0 && (
        <section className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="life-asset-breakdown">
          <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-bold text-stone-500">By category</div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(assets.categories)
              .sort(([, a], [, b]) => Math.abs(b) - Math.abs(a))
              .map(([k, v]) => {
                const meta = CATEGORY_META[k] || CATEGORY_META.other;
                const Icon = meta.Icon;
                return (
                  <div key={k} className="rounded-xl border border-stone-200 p-3 hover:border-emerald-500 transition-colors">
                    <div className={`flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold ${meta.tone}`}>
                      <Icon size={12} /> {meta.label}
                    </div>
                    <div className={`mt-1 font-mono font-black text-lg ${k === "liability" ? "text-red-700" : "text-stone-900"}`}>
                      {inr(v)}
                    </div>
                  </div>
                );
              })}
          </div>
        </section>
      )}

      {/* Cash flow chart — bookkeeper insight at a glance */}
      <CashFlowChart />

      {/* Budget alerts — flag categories trending high vs last month */}
      <BudgetAlerts />

      {/* Bills */}
      <section className="grid md:grid-cols-3 gap-4" data-testid="life-bills">
        <div className="md:col-span-1 space-y-3">
          <div className="bg-white border border-stone-200 rounded-2xl p-5">
            <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 flex items-center gap-1.5"><ReceiptText size={12}/> Bills status</div>
            <div className={`mt-2 text-3xl font-black font-mono ${bills.overdue_count > 0 ? "text-red-700" : "text-stone-900"}`} data-testid="life-bills-unpaid">
              {inr(bills.total_unpaid)}
            </div>
            <div className="mt-1 text-[11px] text-stone-500">
              {bills.unpaid_count} unpaid · <span className="text-red-700 font-bold">{bills.overdue_count} overdue</span>
            </div>
            {bills.overdue_count > 0 && (
              <div className="mt-2 text-[11px] text-red-700 font-bold">Overdue total: {inr(bills.total_overdue)}</div>
            )}
            <Link to="/bills" className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold text-[#E05E36] hover:text-orange-700" data-testid="life-cta-bills">
              Manage bills <ArrowUpRight size={12} />
            </Link>
          </div>
        </div>
        <div className="md:col-span-2 bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-bold text-stone-500">
            Due within 7 days ({bills.due_this_week_count})
          </div>
          {bills.upcoming.length === 0 ? (
            <div className="p-6 text-center text-sm text-stone-500">Nothing due this week 🎉</div>
          ) : (
            <ul className="divide-y divide-stone-100" data-testid="life-bills-list">
              {bills.upcoming.map(b => {
                const overdue = (b.due_date || "") < new Date().toISOString().slice(0, 10);
                return (
                  <li key={b.bill_id} className="px-5 py-3 flex items-center gap-3">
                    <div className={`w-9 h-9 grid place-items-center rounded-xl ${overdue ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
                      <ReceiptText size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-stone-900 truncate">{b.biller || b.kind}</div>
                      <div className="text-[11px] text-stone-500 font-mono">Due {b.due_date}</div>
                    </div>
                    <div className="text-right">
                      <div className={`font-mono font-bold ${overdue ? "text-red-700" : "text-stone-900"}`}>{inr(b.amount_inr)}</div>
                      {b.pay_url && (
                        <a href={b.pay_url} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-[#E05E36] hover:text-orange-700 inline-flex items-center gap-0.5">
                          Pay now <ArrowUpRight size={10} />
                        </a>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* Adult shortcuts */}
      <section className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="life-adult">
        <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 flex items-center gap-1.5"><ShieldCheck size={12}/> Adult-verified shortcuts</div>
          {adult.adult_ready && adult.kyc_expires_at && (
            <div className="text-[10px] text-stone-500 font-mono">KYC valid until {adult.kyc_expires_at.slice(0, 10)}</div>
          )}
        </div>
        {adult.adult_ready ? (
          <div className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3">
            {ADULT_SHORTCUTS.map(s => (
              <Link key={s.label} to={s.to} className="rounded-xl border border-stone-200 p-3 flex flex-col items-center text-center gap-1.5 hover:border-emerald-500 hover:bg-emerald-50 transition-colors group" data-testid={`life-adult-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <s.Icon size={20} className="text-emerald-700 group-hover:scale-110 transition-transform" />
                <div className="text-[11px] font-bold text-stone-800">{s.label}</div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center">
            {adult.kyc_expired ? (
              <>
                <AlertTriangle className="mx-auto text-amber-600 mb-2" size={24} />
                <div className="text-sm text-stone-800 font-bold mb-1">Your 18+ verification has expired</div>
                <div className="text-xs text-stone-500 mb-3">Refresh once every 12 months to keep adult-only tiles unlocked.</div>
                <Link to="/kyc" className="inline-block"><Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="life-cta-kyc-renew">Renew KYC</Button></Link>
              </>
            ) : adult.kyc_status === "verified" && !adult.is_18_plus ? (
              <>
                <ShieldCheck className="mx-auto text-stone-400 mb-2" size={24} />
                <div className="text-sm text-stone-700">Adult-only shortcuts unlock once your KYC date-of-birth confirms you're 18+.</div>
              </>
            ) : (
              <>
                <ShieldCheck className="mx-auto text-stone-400 mb-2" size={24} />
                <div className="text-sm text-stone-700 font-bold mb-1">Verify your KYC to unlock adult shortcuts</div>
                <div className="text-xs text-stone-500 mb-3">One-time 18+ verification enables Dating, Matrimonial, Gyms, Hotels, Concerts and more.</div>
                <Link to="/kyc" className="inline-block"><Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="life-cta-kyc-start">Start KYC</Button></Link>
              </>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-white/70 uppercase tracking-widest text-[10px] font-bold">{label}</div>
      <div className="font-bold text-lg font-mono">{value}</div>
    </div>
  );
}
